import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Typography } from "@aviary";
import { useBreakpoints } from "@shared/hooks";
import {
  PatientStoreSignUpForm,
  SignUpModalHeader,
  SignUpModalTitle,
  StoreLandingLayout,
  StoreLogo,
  StoreOffersPill,
  StoreSignupInCarousel,
} from "@unauthenticated/signupSignin/components";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./PatientStoreSignUpPage.styles";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  pageVersion?: string;
  storeId?: string;
  isPreview?: boolean;
  isStoreOwner: boolean;
  newSSOErrors?: string;
}

const PatientStoreSignUpPage: FC<Props> = ({
  patientAttributes,
  additionalAttributes,
  pageVersion,
  isPreview = false,
  isStoreOwner,
  storeId,
  newSSOErrors,
}) => {
  const { t } = useTranslation();

  const { phoneLarge } = useBreakpoints();

  const { storeLogo, storeName, pracName, pracAvatar } = patientAttributes;

  return (
    <StoreLandingLayout
      pracName={pracName}
      pracAvatar={pracAvatar}
      isPreview={isPreview}
      isStoreOwner={isStoreOwner}
    >
      <SignUpModalHeader>
        <StoreLogo storeLogo={storeLogo} storeName={storeName} />
        <SignUpModalTitle title={storeName} />
        <StoreOffersPill patientAttributes={patientAttributes} />
        {!phoneLarge.greaterThan && (
          <Arrange justify="center" spacing="xlarge">
            <Typography type="h4" css={styles.text}>
              {t(l.patientRxAuth.storeLandingPages.leftColumn.tips, {
                pracName,
                interpolation: { escapeValue: false },
              })}
            </Typography>
            <StoreSignupInCarousel />
          </Arrange>
        )}
      </SignUpModalHeader>
      <PatientStoreSignUpForm
        storeId={storeId}
        pageVersion={pageVersion}
        patientAttributes={patientAttributes}
        additionalAttributes={additionalAttributes}
        isPreview={isPreview}
        newSSOErrors={newSSOErrors}
      />
    </StoreLandingLayout>
  );
};

export { PatientStoreSignUpPage };
